import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CloudIcon,
  BoltIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CogIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ScaleIcon,
  PhoneIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  RocketLaunchIcon,
  ChevronRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const tradingImage = '/images/new-images/hero-trading-platform.jpg';
const analysisImage = '/images/new-images/data-analytics.jpg';
const teamImage = '/images/new-images/team-collaboration.jpg';
const cryptoImage = '/images/new-images/crypto-trading.jpg';

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  const heroServices = [
    {
      title: 'Professional Trading Platform',
      description: 'Advanced trading tools with institutional-grade features',
      icon: ChartBarIcon,
      stats: '10ms Execution',
      gradient: 'from-blue-500 to-blue-700'
    },
    {
      title: 'AI-Powered Analytics',
      description: 'Machine learning insights for smarter trading decisions',
      icon: CpuChipIcon,
      stats: '99.7% Accuracy',
      gradient: 'from-purple-500 to-purple-700'
    },
    {
      title: 'Global Market Access',
      description: 'Trade across 50+ exchanges worldwide',
      icon: GlobeAltIcon,
      stats: '10,000+ Assets',
      gradient: 'from-green-500 to-green-700'
    },
    {
      title: 'Bank-Level Security',
      description: 'Military-grade encryption and cold storage',
      icon: ShieldCheckIcon,
      stats: '256-bit SSL',
      gradient: 'from-red-500 to-red-700'
    }
  ];

  const tradingServices = [
    {
      category: 'Equity Trading',
      title: 'Stock Trading Excellence',
      description: 'Access to global equity markets with real-time data, advanced order types, and comprehensive research tools.',
      features: [
        'Real-time Level II market data',
        'Pre & after-hours trading',
        'Fractional share investing',
        'Options trading strategies',
        'IPO access & allocations',
        'Dividend reinvestment plans'
      ],
      image: tradingImage,
      stats: { volume: '$8.5B', markets: '25+', stocks: '5,000+' }
    },
    {
      category: 'Cryptocurrency',
      title: 'Digital Asset Trading',
      description: 'Comprehensive cryptocurrency trading with 24/7 access, advanced security, and institutional-grade infrastructure.',
      features: [
        '200+ cryptocurrencies',
        '24/7 trading availability',
        'Cold storage security',
        'DeFi token support',
        'Staking rewards program',
        'Crypto derivatives trading'
      ],
      image: cryptoImage,
      stats: { volume: '$2.8B', currencies: '200+', uptime: '99.99%' }
    },
    {
      category: 'Forex Trading',
      title: 'Foreign Exchange Markets',
      description: 'Trade major and exotic currency pairs with tight spreads, high leverage, and expert market analysis.',
      features: [
        '80+ currency pairs',
        'Spreads from 0.1 pips',
        'Leverage up to 1:500',
        'Economic calendar integration',
        'Central bank news feeds',
        'Automated trading systems'
      ],
      image: analysisImage,
      stats: { volume: '$1.2B', pairs: '80+', spread: '0.1 pips' }
    }
  ];

  const platformFeatures = [
    {
      icon: ComputerDesktopIcon,
      title: 'Web Trading Platform',
      description: 'Professional browser-based platform with advanced charting, real-time data, and customizable layouts.',
      features: ['No downloads required', 'Advanced charting tools', 'Real-time market data', 'Custom dashboards']
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Mobile Trading Apps',
      description: 'Full-featured mobile applications for iOS and Android with all desktop capabilities.',
      features: ['Native mobile apps', 'Touch-optimized interface', 'Push notifications', 'Biometric security']
    },
    {
      icon: CloudIcon,
      title: 'API Integration',
      description: 'Comprehensive REST and WebSocket APIs for algorithmic trading and custom applications.',
      features: ['RESTful endpoints', 'WebSocket feeds', 'Python & JS SDKs', 'Sandbox environment']
    },
    {
      icon: CpuChipIcon,
      title: 'Algorithmic Trading',
      description: 'Build and deploy automated trading strategies with our sophisticated algo trading platform.',
      features: ['Strategy builder', 'Backtesting engine', 'Risk management', 'Performance analytics']
    }
  ];

  const supportServices = [
    {
      icon: UserGroupIcon,
      title: 'Dedicated Account Managers',
      description: 'Personal relationship managers for high-value accounts with direct access and priority support.',
      availability: '24/5 Business Hours'
    },
    {
      icon: PhoneIcon,
      title: '24/7 Technical Support',
      description: 'Round-the-clock technical assistance via phone, chat, and email with guaranteed response times.',
      availability: '24/7 Global Coverage'
    },
    {
      icon: AcademicCapIcon,
      title: 'Educational Resources',
      description: 'Comprehensive learning center with webinars, tutorials, and market analysis for all skill levels.',
      availability: 'On-Demand Access'
    },
    {
      icon: CalendarDaysIcon,
      title: 'Market Research',
      description: 'Daily market analysis, economic calendars, and expert insights from our research team.',
      availability: 'Daily Updates'
    }
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      description: 'Perfect for new traders',
      monthlyFee: '$0',
      commissions: '$0.50 per trade',
      features: [
        'Basic trading platform',
        'Real-time market data',
        'Email support',
        'Educational resources',
        'Mobile app access'
      ],
      highlight: false
    },
    {
      name: 'Professional',
      description: 'For active traders',
      monthlyFee: '$49',
      commissions: '$0.25 per trade',
      features: [
        'Advanced trading platform',
        'Level II market data',
        'Priority phone support',
        'Advanced charting tools',
        'Research reports',
        'Options trading'
      ],
      highlight: true
    },
    {
      name: 'Institutional',
      description: 'For high-volume traders',
      monthlyFee: 'Custom',
      commissions: 'Negotiable',
      features: [
        'White-label solutions',
        'Dedicated account manager',
        'Custom API access',
        'Institutional rates',
        'Prime brokerage services',
        'Custom integrations'
      ],
      highlight: false
    }
  ];

  const complianceFeatures = [
    {
      icon: ScaleIcon,
      title: 'Regulatory Compliance',
      description: 'Fully licensed and regulated across multiple jurisdictions with strict adherence to financial regulations.',
      certifications: ['FINRA', 'SEC', 'FCA', 'CySEC']
    },
    {
      icon: ShieldCheckIcon,
      title: 'Security Standards',
      description: 'Industry-leading security measures including encryption, multi-factor authentication, and cold storage.',
      certifications: ['ISO 27001', 'SOC 2', 'PCI DSS', 'GDPR']
    },
    {
      icon: BanknotesIcon,
      title: 'Client Protection',
      description: 'Comprehensive insurance coverage and segregated client funds for maximum protection.',
      certifications: ['SIPC', 'FSCS', 'Up to $500M', 'Segregated Funds']
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/30"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{ backgroundImage: `url(${tradingImage})` }}
          ></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8">
                Trading Services Built for
                <span className="block text-transparent bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text">
                  Excellence
                </span>
              </h1>
              <p className="text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
                Experience the full spectrum of professional trading services designed to 
                give you the competitive edge in global financial markets.
              </p>
            </motion.div>
          </div>

          {/* Hero Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {heroServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="group"
              >
                <div className="card-dark border-white/20 hover:border-white/40 transition-all duration-300 transform hover:-translate-y-2">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-400 font-bold">{service.stats}</span>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Services */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Trading Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access global markets across multiple asset classes with institutional-grade 
              infrastructure and professional trading tools.
            </p>
          </motion.div>

          <div className="space-y-24">
            {tradingServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-600 text-sm font-medium mb-6">
                    {service.category}
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                    {service.title}
                  </h3>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-3" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-6 mb-8">
                    {Object.entries(service.stats).map(([key, value], idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-2xl font-bold text-primary-600">{value}</div>
                        <div className="text-sm text-gray-500 capitalize">{key}</div>
                      </div>
                    ))}
                  </div>

                  <button className="btn-primary">
                    Start Trading Now
                    <RocketLaunchIcon className="ml-2 h-5 w-5" />
                  </button>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="rounded-xl shadow-2xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent rounded-xl"></div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                          <span className="text-sm font-medium text-gray-900">Live Trading</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Multi-Platform Trading Experience
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trade seamlessly across web, mobile, and API platforms with synchronized 
              data and unified portfolio management.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformFeatures.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="card hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 text-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-200 transition-colors duration-300">
                    <platform.icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{platform.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{platform.description}</p>
                  <div className="space-y-2">
                    {platform.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-center">
                        <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              World-Class Support Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive support ecosystem designed to help you succeed in your trading journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {supportServices.map((support, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="card-dark border-gray-700 hover:border-primary-500 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <support.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{support.title}</h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">{support.description}</p>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 text-primary-400 mr-2" />
                    <span className="text-primary-400 text-sm font-medium">{support.availability}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Transparent Pricing Plans
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your trading style. No hidden fees, no surprises.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group"
              >
                <div className={`card relative hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                  tier.highlight ? 'ring-2 ring-primary-500 scale-105' : ''
                }`}>
                  {tier.highlight && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{tier.name}</h3>
                    <p className="text-gray-600 mb-6">{tier.description}</p>
                    
                    <div className="mb-6">
                      <div className="text-4xl font-bold text-gray-900 mb-2">{tier.monthlyFee}</div>
                      <div className="text-sm text-gray-500">monthly platform fee</div>
                      <div className="text-lg font-semibold text-primary-600 mt-2">{tier.commissions}</div>
                    </div>

                    <div className="space-y-4 mb-8">
                      {tier.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-3" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className={`w-full py-3 rounded-lg font-semibold transition-colors duration-300 ${
                      tier.highlight 
                        ? 'bg-primary-600 text-white hover:bg-primary-700' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}>
                      Get Started
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance & Security */}
      <section className="py-24 bg-primary-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Regulated & Secure
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Your security and compliance are our top priorities. We maintain the highest 
              standards of regulatory compliance and data protection.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {complianceFeatures.map((compliance, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <compliance.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{compliance.title}</h3>
                <p className="text-primary-100 mb-6 leading-relaxed">{compliance.description}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {compliance.certifications.map((cert, idx) => (
                    <span key={idx} className="bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {cert}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-8">
              Ready to Experience Professional Trading?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join over 250,000 investors who trust INTRA CAPITAL LIMITED with their financial future. 
              Start your journey with the most advanced investment platform available.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="bg-primary-600 text-white hover:bg-primary-700 px-10 py-4 rounded-lg font-bold text-lg transition-colors duration-300"
              >
                Start Trading Today
                <RocketLaunchIcon className="ml-3 h-6 w-6 inline" />
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300"
              >
                <PlayIcon className="mr-3 h-6 w-6 inline" />
                Watch Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services; 